import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

/**
 * A labelled input with its error wired up for screen readers.
 *
 * The `aria-describedby` / `aria-invalid` pair is the part that matters: without
 * it a validation message is visible but never announced, so a screen-reader
 * user is told only that the form failed, not which field or why.
 */
export function Field({ label, error, hint, className, ...props }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium text-surface-300">
        {label}
      </label>

      <input
        {...props}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(error && errorId, hint && hintId) || undefined}
        className={cn(
          'form-input text-surface-100 outline-none transition',
          'placeholder:text-surface-600',
          error
            ? 'border-red-500/70 focus:border-red-400'
            : 'border-surface-700 focus:border-brand-500',
          className,
        )}
      />

      {hint && !error ? (
        <p id={hintId} className="text-sm text-surface-500">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="text-sm text-red-400">
          {error}
        </p>
      ) : null}
    </div>
  );
}
